import React from 'react'
import { useNavigate } from 'react-router-dom'

import { useEncodingDevice } from 'contexts'
import { ROUTES } from 'routes'
import { SHOP_PRODUCT_TYPE } from 'constants/tagShopProductTypes'

import { Box, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import {
  CheckIcon,
  CircleHelpIcon,
  DownloadIcon,
  MessageCircleIcon,
  RadioReceiverIcon,
  ShoppingBagIcon,
} from 'lucide-react'

import { Card } from '@repo/ui'
import { Button } from '@repo/ui'
import { Header } from 'components'

// The driver for the underlying ACS reader hardware. This is the only real, downloadable
// driver today, so it's what the "Download driver" button in step 2 points at.
// Verified live — see acs.com.hk.
const READER_DRIVERS_URL = 'https://www.acs.com.hk/en/drivers/'

/** A stepper node — solid green + white check when done, outlined + number when pending. */
const StepNode = ({ done, index }: { done: boolean; index: number }) => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        flexShrink: 0,
        width: 26,
        height: 26,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 600,
        ...(done
          ? { backgroundColor: theme.palette.brand[500], color: theme.palette.common.white }
          : {
              backgroundColor: 'transparent',
              color: theme.palette.text.secondary,
              border: `1.5px solid ${theme.palette.primary[200]}`,
            }),
      }}
    >
      {done ? <CheckIcon strokeWidth={3} size={14} /> : index}
    </Box>
  )
}

/**
 * Reader setup guide (VERA-455). The onboarding / "Learn more" target for the hardware
 * reader: what it is, how to get one, install the driver, and scan. Reachable at
 * /support/reader-setup and linked from the sidebar reader popover and the
 * Create-certificates modal's reader section.
 *
 * The three steps render as a connected vertical stepper whose nodes + rail reflect the
 * LIVE reader state, so the page doubles as a status check.
 */
export const ReaderSetup = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { helperReachable, isConnected } = useEncodingDevice()

  const hasReader = helperReachable || isConnected

  const steps: { title: string; done: boolean; content: React.ReactNode }[] = [
    {
      title: 'Get a reader',
      done: hasReader,
      content: (
        <>
          <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.5 }}>
            Get the Vera Desk Reader from the shop for tabletop scanning, or the Vera Assembly
            Line Reader for higher-volume setups.
          </Typography>
          <Box>
            <Button
              variant="secondary"
              disableElevation
              startIcon={<ShoppingBagIcon strokeWidth={1} size={18} />}
              // Same destination as ReaderSetupPanel's "Shop readers": the shop narrowed to
              // Shopify productType "Hardware". This page is where that panel's "Learn more"
              // link lands, so the two buttons must not disagree about where readers live.
              onClick={() => navigate(ROUTES.TAGS_SHOP_BY_TYPE(SHOP_PRODUCT_TYPE.HARDWARE))}
            >
              Shop Vera readers
            </Button>
          </Box>
        </>
      ),
    },
    {
      title: 'Install the Vera Reader Driver',
      done: hasReader,
      content: (
        <>
          <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.5 }}>
            The driver lets your computer talk to the reader. Install it before plugging the
            reader in, then restart your machine so the new driver takes effect. On macOS the
            built-in driver can claim the reader without being able to read tags, so installing
            this one is required rather than optional.
          </Typography>
          <Box>
            <Button
              variant="secondary"
              disableElevation
              component="a"
              href={READER_DRIVERS_URL}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<DownloadIcon strokeWidth={1} size={18} />}
            >
              Download Vera Reader Driver
            </Button>
          </Box>
        </>
      ),
    },
    {
      title: 'Plug in and scan',
      done: isConnected,
      content: (
        <>
          <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.5 }}>
            Connect the reader over USB. In any product, click Create Certificates, choose Scan
            with a hardware reader, and tap each Vera Tag to the reader. Every tag creates a
            certificate as you go.
          </Typography>
          <Stack direction="row" spacing={3} alignItems="center">
            <RadioReceiverIcon
              strokeWidth={1.5}
              size={18}
              color={isConnected ? theme.palette.brand[500] : theme.palette.text.secondary}
            />
            <Typography
              variant="body4"
              sx={{ fontSize: 12, color: isConnected ? theme.palette.brand[500] : theme.palette.text.secondary }}
            >
              The reader menu in the sidebar turns purple when a reader is connected.
            </Typography>
          </Stack>
        </>
      ),
    },
  ]

  return (
    // spacing={0} + explicit `mt` per section: the gaps here are deliberately UNEVEN
    // (tight title→intro, roomier intro→steps and steps→support), which a single uniform
    // Stack spacing can't express.
    <Stack spacing={0} sx={{ maxWidth: 760, width: '100%', mx: 'auto' }}>
      {/* The hand-rolled "← Back" that used to sit above this Header is gone, along with the
          `mt: 4` that offset the Header beneath it. Two reasons, one visual and one structural:
          it pushed the breadcrumb to 79.6 when a page's wayfinding row belongs on the sidebar
          logo's 44, and the breadcrumb it sat above is already this page's way back — the repo's
          single back affordance for hierarchical navigation.

          Header's default pb:6 (24px) on top of the section gap left the title floating well
          clear of its own description, so pb is still zeroed here. */}
      <Header
        breadcrumbsOveride="Set up a hardware reader"
        title="Set up a hardware reader"
        sx={{ pb: 0 }}
      />

      {/* Intro — borderless (not a card), no icon. The description sits directly under the
          page title; a second "Scan tags without a phone" heading above it read as a
          duplicate title on a page that already has one. */}
      <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.5, mt: 4 }}>
        In addition to creating certificates with your phone via the Vera companion app, Vera
        offers desktop and high-speed hardware readers for higher-volume encoding jobs. These
        readers are available for purchase in the tag shop and plug directly into your computer
        to encode tags. Follow the instructions below to get set up and start encoding at volume.
      </Typography>

      {/* Setup steps — a connected vertical stepper; nodes + rail track the live state. */}
      <Card sx={{ mt: 8 }}>
        <Stack spacing={0}>
          {steps.map((step, i) => {
            const isLast = i === steps.length - 1
            return (
              <Box key={step.title} sx={{ display: 'flex', gap: 4 }}>
                {/* Rail: node + the connecting line to the next node (green once done). */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <StepNode done={step.done} index={i + 1} />
                  {!isLast && (
                    <Box
                      sx={{
                        flexGrow: 1,
                        width: '2px',
                        minHeight: 20,
                        my: 1,
                        borderRadius: 1,
                        backgroundColor: step.done ? theme.palette.brand[400] : theme.palette.primary[100],
                      }}
                    />
                  )}
                </Box>
                {/* Content */}
                <Stack spacing={3} sx={{ flex: 1, pb: isLast ? 0 : 5, pt: '2px' }}>
                  <Typography variant="body1" sx={{ fontSize: 18 }}>
                    {step.title}
                  </Typography>
                  {step.content}
                </Stack>
              </Box>
            )
          })}
        </Stack>
      </Card>

      {/* Troubleshooting — borderless (not a card). The old section walked through the one
          macOS driver caveat; that now lives in the driver step itself, so anything left over
          is a real support conversation rather than a documented fix. */}
      <Stack spacing={3} sx={{ mt: 10 }}>
        <Stack direction="row" spacing={3} alignItems="center">
          <CircleHelpIcon strokeWidth={1.5} size={20} color={theme.palette.text.secondary} />
          <Typography variant="body1" sx={{ fontSize: 16 }}>
            Having issues?
          </Typography>
        </Stack>
        <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.5 }}>
          If your reader still isn't working after following the steps above, get in touch and
          we'll help you get set up.
        </Typography>
        <Box>
          <Button
            variant="secondary"
            disableElevation
            startIcon={<MessageCircleIcon strokeWidth={1} size={18} />}
            onClick={() => navigate(ROUTES.SUPPORT_NEW)}
          >
            Contact support
          </Button>
        </Box>
      </Stack>
    </Stack>
  )
}
